import React, { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check as ApproveIcon,
  EventRepeat as RunIcon,
  PostAdd as PostIcon,
  Visibility as ViewIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Undo as ReverseIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../hooks/useAccessControl';
import { ACTIONS, RESOURCES } from '../../hooks/usePermissions';
import { ledgerAPI } from '../../services/api';
import { formatAccountingAmount } from '../../utils/formatters';
import { useAppSnackbar, useConfirmDialog } from '../common/TableDesign';
import LedgerListPage from './LedgerListPage';
import { AccountPicker, CurrencyPicker, FiscalPeriodPicker } from './LedgerPickers';

const KINDS = ['ACCRUED_EXPENSE', 'ACCRUED_REVENUE', 'PREPAID_EXPENSE', 'DEFERRED_REVENUE'];
const STATUSES = ['DRAFT', 'ACTIVE', 'COMPLETED', 'CANCELLED'];
const unwrap = (response) => response?.data?.data ?? response?.data ?? {};
const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-GB') : '—');

const emptyForm = () => ({
  kind: 'ACCRUED_EXPENSE', description: '', reference: '', debit_account_id: '', credit_account_id: '',
  settlement_account_id: '', currency_id: '', exchange_rate: '', total_amount: '', monthly_amount: '',
  start_date: new Date().toISOString().slice(0, 10), end_date: '', auto_reverse: false,
});

const AccrualStatus = ({ status }) => (
  <Chip size="small" variant="outlined" label={status || '—'}
    color={status === 'ACTIVE' || status === 'COMPLETED' ? 'success' : status === 'CANCELLED' ? 'default' : 'warning'} />
);

/** Accruals/deferrals lifecycle backed by the standalone ledger API. */
const Accruals = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.GL, ACTIONS.CREATE);
  const canUpdate = can(RESOURCES.LEDGER.GL, ACTIONS.UPDATE);
  const canApprove = can(RESOURCES.LEDGER.GL, ACTIONS.APPROVE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [recognizing, setRecognizing] = useState(null);
  const [recognitionForm, setRecognitionForm] = useState({ date: '', amount: '', period_id: '' });
  const [reverseTarget, setReverseTarget] = useState(null);
  const [reverseReason, setReverseReason] = useState('');
  const [asOfOpen, setAsOfOpen] = useState(false);
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [running, setRunning] = useState(false);
  const reload = () => setRefreshKey((key) => key + 1);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (schedule) => {
    setEditing(schedule);
    setForm({
      description: schedule.description || '',
      reference: schedule.reference || '',
      monthly_amount: schedule.monthly_amount ?? '',
    });
    setFormError('');
    setFormOpen(true);
  };

  const saveSchedule = async () => {
    setFormError('');
    if (!String(form.description || '').trim()) return setFormError('Description is required.');
    if (editing) {
      if (!(Number(form.monthly_amount) > 0)) return setFormError('Monthly amount must be greater than zero.');
    } else {
      const total = Number(form.total_amount);
      const monthly = Number(form.monthly_amount);
      if (!(total > 0) || !(monthly > 0)) return setFormError('Total and monthly amounts must be greater than zero.');
      if (monthly > total) return setFormError('Monthly amount cannot exceed the total amount.');
      if (!form.end_date || form.start_date > form.end_date) return setFormError('Enter a valid end date on or after the start date.');
      if (!form.debit_account_id || !form.credit_account_id) return setFormError('Choose both schedule accounts.');
      if (['PREPAID_EXPENSE', 'DEFERRED_REVENUE'].includes(form.kind) && !form.settlement_account_id) {
        return setFormError('Choose a settlement account for the upfront posting.');
      }
    }
    setSaving(true);
    try {
      if (editing) {
        await ledgerAPI.updateAccrualSchedule(editing.schedule_id, {
          description: form.description.trim(),
          reference: form.reference.trim() || undefined,
          monthly_amount: Number(form.monthly_amount),
        });
        showSnackbar('Accrual schedule updated.', 'success');
      } else {
        const payload = {
          kind: form.kind,
          description: form.description.trim(),
          reference: form.reference.trim() || undefined,
          debit_account_id: Number(form.debit_account_id),
          credit_account_id: Number(form.credit_account_id),
          total_amount: Number(form.total_amount),
          monthly_amount: Number(form.monthly_amount),
          start_date: form.start_date,
          end_date: form.end_date,
          auto_reverse: Boolean(form.auto_reverse),
        };
        if (form.settlement_account_id) payload.settlement_account_id = Number(form.settlement_account_id);
        if (form.currency_id) payload.currency_id = Number(form.currency_id);
        if (form.exchange_rate) payload.exchange_rate = Number(form.exchange_rate);
        await ledgerAPI.createAccrualSchedule(payload);
        showSnackbar('Schedule saved as a draft.', 'success');
      }
      setFormOpen(false);
      reload();
    } catch (error) {
      setFormError(error?.response?.data?.message || 'Could not save the accrual schedule.');
    } finally {
      setSaving(false);
    }
  };

  const runDue = async () => {
    setRunning(true);
    try {
      const result = unwrap(await ledgerAPI.runAccruals({ as_of: asOf }));
      const count = result.recognised?.length || 0;
      const skipped = result.skipped || [];
      const message = `Run finished: ${count} schedule(s) recognised, ${result.reversed || 0} reversal(s) posted, ${skipped.length} skipped.`;
      showSnackbar(skipped.length ? `${message} ${skipped[0]?.reason || ''}` : message, skipped.length ? 'warning' : 'success');
      setAsOfOpen(false);
      reload();
    } catch (error) {
      showSnackbar(error?.response?.data?.message || 'Accrual run failed.', 'error');
    } finally {
      setRunning(false);
    }
  };

  const runAction = (title, description, action, successMessage) => confirm({
    title,
    message: description,
    onConfirm: async () => {
      try {
        await action();
        showSnackbar(successMessage, 'success');
        reload();
        if (detail) setDetail(unwrap(await ledgerAPI.getAccrualSchedule(detail.schedule_id)));
      } catch (error) {
        showSnackbar(error?.response?.data?.message || `${title} failed.`, 'error');
      }
    },
  });

  const openDetail = async (row) => {
    try {
      setDetail(unwrap(await ledgerAPI.getAccrualSchedule(row.schedule_id)));
    } catch (error) {
      showSnackbar(error?.response?.data?.message || 'Could not load schedule details.', 'error');
    }
  };

  const submitRecognition = async () => {
    const payload = {};
    if (recognitionForm.date) payload.date = recognitionForm.date;
    if (recognitionForm.amount) payload.amount = Number(recognitionForm.amount);
    if (recognitionForm.period_id) payload.period_id = Number(recognitionForm.period_id);
    try {
      await ledgerAPI.recognizeAccrual(recognizing.schedule_id, payload);
      showSnackbar('Instalment recognised and posted.', 'success');
      setRecognizing(null);
      reload();
      if (detail?.schedule_id === recognizing.schedule_id) openDetail(recognizing);
    } catch (error) {
      showSnackbar(error?.response?.data?.message || 'Recognition failed.', 'error');
    }
  };

  const submitReverse = async () => {
    if (reverseReason.trim().length < 5) return showSnackbar('Enter a reason of at least five characters.', 'error');
    try {
      await ledgerAPI.reverseAccrualRecognition(reverseTarget.recognition_id, { reason: reverseReason.trim() });
      showSnackbar('Recognition reversed.', 'success');
      setReverseTarget(null);
      setReverseReason('');
      reload();
      if (detail) setDetail(unwrap(await ledgerAPI.getAccrualSchedule(detail.schedule_id)));
    } catch (error) {
      showSnackbar(error?.response?.data?.message || 'Recognition reversal failed.', 'error');
    }
  };

  const rowActions = (schedule) => {
    const actions = [{ icon: ViewIcon, title: 'View schedule and recognition history', onClick: () => openDetail(schedule) }];
    if (schedule.status === 'DRAFT' && canUpdate) actions.push({ icon: EditIcon, title: 'Edit draft', onClick: () => openEdit(schedule) });
    if (schedule.status === 'DRAFT' && canApprove) {
      actions.push({ icon: ApproveIcon, title: 'Approve schedule', onClick: () => runAction('Approve accrual schedule', 'Approve this draft schedule and activate it?', () => ledgerAPI.approveAccrualSchedule(schedule.schedule_id), 'Schedule approved and activated.') });
    }
    if (schedule.status === 'ACTIVE' && ['PREPAID_EXPENSE', 'DEFERRED_REVENUE'].includes(schedule.kind) && !schedule.initial_posted && canCreate) {
      actions.push({ icon: PostIcon, title: 'Post upfront entry', onClick: () => runAction('Post upfront entry', 'Post the settlement journal for this prepaid expense or deferred revenue schedule?', () => ledgerAPI.postAccrualInitial(schedule.schedule_id), 'Upfront entry posted.') });
    }
    if (schedule.status === 'ACTIVE' && canCreate) {
      actions.push({ icon: PostIcon, title: 'Recognise instalment', onClick: () => { setRecognizing(schedule); setRecognitionForm({ date: '', amount: '', period_id: '' }); } });
    }
    if (['DRAFT', 'ACTIVE'].includes(schedule.status)
      && Number(schedule.recognised_amount || 0) === 0
      && !(schedule.recognitions?.length)
      && canCreate) {
      actions.push({ icon: CancelIcon, title: 'Cancel schedule', color: 'error', onClick: () => runAction('Cancel accrual schedule', 'Cancel this schedule? A schedule with posted recognitions cannot be cancelled.', () => ledgerAPI.cancelAccrualSchedule(schedule.schedule_id), 'Schedule cancelled.') });
    }
    return actions;
  };

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.GL}
        title="Accruals & Deferrals"
        subtitle="Schedule accruals, prepayments and deferred income; post, recognise and reverse against open fiscal periods."
        addLabel="New Schedule"
        idKey="schedule_id"
        searchEnabled={false}
        emptyMessage="No accrual schedules found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getAccrualSchedules(params)}
        onAdd={canCreate ? openCreate : null}
        extraActions={canCreate ? [{ label: 'Run due schedules', icon: <RunIcon />, onClick: () => setAsOfOpen(true) }] : []}
        filters={[
          { key: 'kind', label: 'Schedule type', options: KINDS.map((kind) => ({ label: kind.replaceAll('_', ' '), value: kind })) },
          { key: 'status', label: 'Status', options: STATUSES.map((status) => ({ label: status, value: status })) },
        ]}
        columns={[
          { label: 'Schedule', render: (row) => <strong>{row.schedule_number || `#${row.schedule_id}`}</strong> },
          { label: 'Type / Description', render: (row) => <>{row.kind?.replaceAll('_', ' ')}<br /><Typography variant="caption" color="text.secondary">{row.description}</Typography></> },
          { label: 'Next due', render: (row) => formatDate(row.next_due_date) },
          { label: 'Total', render: (row) => formatAccountingAmount(row.total_amount), align: 'right' },
          { label: 'Recognised', render: (row) => formatAccountingAmount(row.recognised_amount), align: 'right', hideOnMobile: true },
          { label: 'Status', render: (row) => <AccrualStatus status={row.status} /> },
        ]}
        rowActions={rowActions}
      />

      <Dialog open={formOpen} onClose={() => !saving && setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Draft Schedule' : 'New Accrual / Deferral Schedule'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {!editing && <Grid size={{ xs: 12, md: 6 }}>
              <TextField select fullWidth size="small" label="Schedule type" value={form.kind} onChange={(e) => setForm((v) => ({ ...v, kind: e.target.value }))}>
                {KINDS.map((kind) => <MenuItem key={kind} value={kind}>{kind.replaceAll('_', ' ')}</MenuItem>)}
              </TextField>
            </Grid>}
            <Grid size={{ xs: 12, md: editing ? 12 : 6 }}>
              <TextField fullWidth size="small" label="Description" value={form.description} onChange={(e) => setForm((v) => ({ ...v, description: e.target.value }))} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Reference (optional)" value={form.reference} onChange={(e) => setForm((v) => ({ ...v, reference: e.target.value }))} />
            </Grid>
            {!editing && <>
              <Grid size={{ xs: 12, md: 6 }}>
                <AccountPicker label="Debit account" value={form.debit_account_id} onChange={(id) => setForm((v) => ({ ...v, debit_account_id: id }))} required activeOnly />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <AccountPicker label="Credit account" value={form.credit_account_id} onChange={(id) => setForm((v) => ({ ...v, credit_account_id: id }))} required activeOnly />
              </Grid>
              {['PREPAID_EXPENSE', 'DEFERRED_REVENUE'].includes(form.kind) && <Grid size={{ xs: 12, md: 6 }}>
                <AccountPicker label="Settlement bank / cash account" value={form.settlement_account_id} onChange={(id) => setForm((v) => ({ ...v, settlement_account_id: id }))} required activeOnly />
              </Grid>}
              <Grid size={{ xs: 12, md: 6 }}><CurrencyPicker label="Currency (defaults to base currency)" value={form.currency_id} onChange={(id) => setForm((v) => ({ ...v, currency_id: id }))} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" type="number" label="Exchange rate (optional)" value={form.exchange_rate} onChange={(e) => setForm((v) => ({ ...v, exchange_rate: e.target.value }))} inputProps={{ min: 0, step: 'any' }} /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" type="number" label="Total amount" value={form.total_amount} onChange={(e) => setForm((v) => ({ ...v, total_amount: e.target.value }))} inputProps={{ min: 0.01, step: 'any' }} required /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" type="number" label="Monthly recognition amount" value={form.monthly_amount} onChange={(e) => setForm((v) => ({ ...v, monthly_amount: e.target.value }))} inputProps={{ min: 0.01, step: 'any' }} required /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" type="date" label="Start date" value={form.start_date} onChange={(e) => setForm((v) => ({ ...v, start_date: e.target.value }))} InputLabelProps={{ shrink: true }} required /></Grid>
              <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" type="date" label="End date" value={form.end_date} onChange={(e) => setForm((v) => ({ ...v, end_date: e.target.value }))} InputLabelProps={{ shrink: true }} required /></Grid>
              {form.kind.startsWith('ACCRUED') && <Grid size={12}><FormControlLabel control={<Checkbox checked={form.auto_reverse} onChange={(e) => setForm((v) => ({ ...v, auto_reverse: e.target.checked }))} />} label="Automatically reverse each accrual on the first day of the next month" /></Grid>}
            </>}
            {editing && <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" type="number" label="Monthly recognition amount" value={form.monthly_amount} onChange={(e) => setForm((v) => ({ ...v, monthly_amount: e.target.value }))} inputProps={{ min: 0.01, step: 'any' }} required /></Grid>}
          </Grid>
          {formError && <Alert severity="error" sx={{ mt: 2 }}>{formError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveSchedule} disabled={saving}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Save draft'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={asOfOpen} onClose={() => !running && setAsOfOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Run due accruals</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>Recognise active schedules due on or before this date and retry any pending automatic reversals.</Typography>
          <TextField fullWidth size="small" type="date" label="As of date" value={asOf} onChange={(e) => setAsOf(e.target.value)} InputLabelProps={{ shrink: true }} />
        </DialogContent>
        <DialogActions><Button onClick={() => setAsOfOpen(false)} disabled={running}>Cancel</Button><Button variant="contained" onClick={runDue} disabled={running}>{running ? 'Running…' : 'Run schedules'}</Button></DialogActions>
      </Dialog>

      <Dialog open={!!recognizing} onClose={() => setRecognizing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Recognise next instalment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>{recognizing?.schedule_number} · {recognizing?.description}. Leave date/amount blank to use the next due date and configured monthly amount.</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" type="date" label="Recognition date (optional)" value={recognitionForm.date} onChange={(e) => setRecognitionForm((v) => ({ ...v, date: e.target.value }))} InputLabelProps={{ shrink: true }} /></Grid>
            <Grid size={{ xs: 12, md: 6 }}><TextField fullWidth size="small" type="number" label="Amount (optional)" value={recognitionForm.amount} onChange={(e) => setRecognitionForm((v) => ({ ...v, amount: e.target.value }))} inputProps={{ min: 0.01, step: 'any' }} /></Grid>
            <Grid size={12}><FiscalPeriodPicker label="Post to fiscal period (optional)" value={recognitionForm.period_id} onChange={(id) => setRecognitionForm((v) => ({ ...v, period_id: id }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions><Button onClick={() => setRecognizing(null)}>Cancel</Button><Button variant="contained" onClick={submitRecognition}>Post recognition</Button></DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>{detail?.schedule_number || 'Accrual schedule'}{detail?.description ? ` · ${detail.description}` : ''}</DialogTitle>
        <DialogContent>
          {detail && <>
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid size={{ xs: 12, md: 6 }}><Typography variant="body2">Type: {detail.kind?.replaceAll('_', ' ')}</Typography></Grid>
              <Grid size={{ xs: 12, md: 6 }}><Typography variant="body2">Status: <AccrualStatus status={detail.status} /></Typography></Grid>
              <Grid size={{ xs: 12, md: 6 }}><Typography variant="body2">Schedule: {formatDate(detail.start_date)} – {formatDate(detail.end_date)}</Typography></Grid>
              <Grid size={{ xs: 12, md: 6 }}><Typography variant="body2">Next due: {formatDate(detail.next_due_date)}</Typography></Grid>
              <Grid size={{ xs: 12, md: 6 }}><Typography variant="body2">Total / recognised: {formatAccountingAmount(detail.total_amount)} / {formatAccountingAmount(detail.recognised_amount)}</Typography></Grid>
              <Grid size={{ xs: 12, md: 6 }}><Typography variant="body2">Monthly amount: {formatAccountingAmount(detail.monthly_amount)}</Typography></Grid>
              <Grid size={12}><Typography variant="body2">Accounts: {detail.debitAccount?.account_code} {detail.debitAccount?.account_name} → {detail.creditAccount?.account_code} {detail.creditAccount?.account_name}</Typography></Grid>
              {detail.initial_entry_id && <Grid size={12}><Typography variant="body2">Upfront journal entry: #{detail.initial_entry_id}</Typography></Grid>}
              {detail.auto_reverse && <Grid size={12}><Typography variant="body2">Automatic reversal enabled{detail.recognitions?.some((r) => r.reversal_pending) ? ' · one or more reversals are pending' : ''}</Typography></Grid>}
            </Grid>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Recognition history</Typography>
            {detail.recognitions?.length ? <Table size="small"><TableHead><TableRow><TableCell>Date</TableCell><TableCell>Amount</TableCell><TableCell>Status</TableCell><TableCell>Journal</TableCell><TableCell>Reversal</TableCell><TableCell /></TableRow></TableHead><TableBody>
              {detail.recognitions.map((r) => <TableRow key={r.recognition_id}>
                <TableCell>{formatDate(r.recognition_date)}</TableCell>
                <TableCell>{formatAccountingAmount(r.amount)}</TableCell>
                <TableCell><AccrualStatus status={r.status} /></TableCell>
                <TableCell>{r.journal_entry_id ? `#${r.journal_entry_id}` : '—'}</TableCell>
                <TableCell>{r.reversal_entry_id ? `#${r.reversal_entry_id}` : r.reversal_pending ? 'Pending' : '—'}</TableCell>
                <TableCell>{r.status === 'POSTED' && canApprove && <Button size="small" color="error" startIcon={<ReverseIcon />} onClick={() => { setReverseTarget(r); setReverseReason(''); }}>Reverse</Button>}</TableCell>
              </TableRow>)}
            </TableBody></Table> : <Typography variant="body2" color="text.secondary">No recognitions posted yet.</Typography>}
          </>}
        </DialogContent>
        <DialogActions>
          {detail?.status === 'ACTIVE' && canCreate && <Button onClick={() => { setRecognizing(detail); setRecognitionForm({ date: '', amount: '', period_id: '' }); }}>Recognise instalment</Button>}
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!reverseTarget} onClose={() => setReverseTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reverse recognition</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>This creates a reversing journal entry and reopens the schedule amount. Explain why the posting must be reversed.</Typography>
          <TextField fullWidth multiline minRows={3} label="Reason (at least 5 characters)" value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} />
        </DialogContent>
        <DialogActions><Button onClick={() => setReverseTarget(null)}>Cancel</Button><Button color="error" variant="contained" onClick={submitReverse}>Reverse posting</Button></DialogActions>
      </Dialog>

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default Accruals;
